import React, { useState } from "react";
import {
  Tooltip,
  Box,
  Stack,
  IconButton,
  MenuItem,
  Divider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  editObject,
  deleteObject,
  tap,
  animatorHoverEnter,
  animatorHoverLeave,
} from "../reducers/slices/phases.js";
import { changeTab } from "../reducers/slices/settings.js";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableMoreMenu from "../../components/table/TableMoreMenu.js";
import Iconify from "../../components/Iconify.js";
import ObjectSettings from "./ObjectSettings.js";
import {
  faChalkboard,
  faHistory,
  faOption,
  faGripVertical,
  faExclamationTriangle,
  faCheck,
  faArrowPointer,
} from "@fortawesome/pro-solid-svg-icons";

import { nonMovingActions } from "../functions/AnimationFunctions.js";

function AnimationAction(props) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <Box sx={{ pb: 1 }}>
      <Box
        sx={(theme) => ({
          backgroundColor: "white",
          position: "relative",
          zIndex: 1,
        })}
      >
        <Stack direction="row">
          <Box
            onMouseEnter={() => {
              console.log("mouse enter");
              dispatch(
                animatorHoverEnter({
                  id: props.step.action.id,
                })
              );
            }}
            onMouseLeave={() => {
              dispatch(animatorHoverLeave({ id: props.step.action.id }));
            }}
            onClick={props.onClick}
            sx={{
              py: 1.3,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              touchAction: "none",
            }}
            {...props.dragHandleProps}
            style={{ touchAction: "none" }}
          >
            <FontAwesomeIcon
              style={{
                touchAction: "none",
                fontSize: 26,
                lineHeight: "24px",
                cursor: "move",
                verticalAlign: "middle",
              }}
              icon={faGripVertical}
            />
          </Box>

          <div
            onMouseEnter={() => {
              console.log("mouse enter");
              dispatch(
                animatorHoverEnter({
                  id: props.step.action.id,
                })
              );
            }}
            onMouseLeave={() => {
              dispatch(animatorHoverLeave({ id: props.step.action.id }));
            }}
            style={{
              cursor: "pointer",
              flex: 1,
              alignItems: "center",
              display: "flex",
            }}
          >
            <Stack
              direction="row"
              sx={{
                flex: 1,
                fontSize: 12,
                mr: 1,
              }}
            >
              {props.step.error && (
                <Tooltip
                  disableInteractive
                  enterDelay={0}
                  placement="top"
                  title={props.step.error}
                >
                  <div style={{ marginRight: 10 }}>
                    <FontAwesomeIcon
                      style={{
                        color: theme.palette.error.main,
                        fontSize: 20,
                        verticalAlign: "middle",
                      }}
                      icon={faExclamationTriangle}
                    />
                  </div>
                </Tooltip>
              )}
              {props.step.description}
            </Stack>
          </div>

          {nonMovingActions.includes(props.step.action.subType) && (
            <div
              style={{
                marginRight: -5,
                display: "flex",
                cursor: "pointer",
                alignItems: "center",
              }}
            >
              <ObjectSettings action={props.step.action} />
            </div>
          )}
          {!nonMovingActions.includes(props.step.action.subType) && (
            <>
              <Tooltip
                disableInteractive
                enterDelay={500}
                placement="top"
                sx={{ p: 3 }}
                arrow
                title={"Execute at same time as previous action"}
              >
                <div
                  onClick={() => {
                    dispatch(
                      editObject({
                        actionId: props.step.action.id,
                        changes: { sameTime: !props.step.action.sameTime },
                      })
                    );
                  }}
                  style={{
                    marginRight: -5,
                    display: "flex",
                    cursor: "pointer",
                    alignItems: "center",
                  }}
                >
                  <IconButton>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 25,
                        height: 25,
                      }}
                    >
                      <FontAwesomeIcon
                        style={{
                          fontSize: 18,
                          color: props.step.action.sameTime
                            ? theme.palette.grey[800]
                            : theme.palette.grey[400],
                        }}
                        icon={faHistory}
                      />
                    </Box>
                  </IconButton>
                </div>
              </Tooltip>

              <Tooltip
                arrow
                disableInteractive
                enterDelay={500}
                placement="top"
                title={
                  "Optional Action (draws an arrow but doesn't move players/ball)"
                }
              >
                <div
                  onClick={() => {
                    dispatch(
                      editObject({
                        actionId: props.step.action.id,
                        changes: { option: !props.step.action.option },
                      })
                    );
                  }}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 25,
                        height: 25,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faOption}
                        style={{
                          fontSize: 18,
                          color: props.step.action.option
                            ? theme.palette.grey[800]
                            : theme.palette.grey[400],
                        }}
                      />
                    </Box>
                  </IconButton>
                </div>
              </Tooltip>
            </>
          )}

          <Box sx={{ mt: "2px", mr: 0.5 }}>
            <TableMoreMenu
              popoverSx={{ mt: -1 }}
              width={200}
              open={openMenu}
              onOpen={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenMenu(event.currentTarget);
              }}
              onClose={() => setOpenMenu(false)}
              actions={
                <>
                  <MenuItem
                    onClick={() => {
                      props.onClick();
                      setOpenMenu(false);
                    }}
                  >
                    <FontAwesomeIcon
                      style={{ fontSize: 18 }}
                      icon={faArrowPointer}
                    />
                    Show {props.step.action.subType}
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      dispatch(tap({ id: props.step.action.id }));
                      dispatch(changeTab(0));
                    }}
                  >
                    <FontAwesomeIcon
                      style={{ fontSize: 18 }}
                      icon={faChalkboard}
                    />
                    Edit {props.step.action.subType}
                  </MenuItem>

                  {nonMovingActions.includes(props.step.action.subType) && (
                    <MenuItem
                      onClick={() => {
                        dispatch(
                          editObject({
                            actionId: props.step.action.id,
                            changes: { sameTime: !props.step.action.sameTime },
                          })
                        );
                      }}
                    >
                      <FontAwesomeIcon
                        style={{ fontSize: 18 }}
                        icon={faHistory}
                      />
                      Make synchronous
                      {props.step.action.sameTime && (
                        <Box sx={{ color: "primary.main" }}>
                          <FontAwesomeIcon
                            icon={faCheck}
                            style={{ height: 16, marginLeft: 10 }}
                          />
                        </Box>
                      )}
                    </MenuItem>
                  )}

                  {!nonMovingActions.includes(props.step.action.subType) && (
                    <MenuItem
                      onClick={() => {
                        dispatch(
                          editObject({
                            actionId: props.step.action.id,
                            changes: { option: !props.step.action.option },
                          })
                        );
                      }}
                    >
                      <FontAwesomeIcon
                        style={{ fontSize: 18 }}
                        icon={faOption}
                      />
                      Make optional
                      {props.step.action.option && (
                        <Box sx={{ color: "primary.main" }}>
                          <FontAwesomeIcon
                            icon={faCheck}
                            style={{ height: 16, marginLeft: 10 }}
                          />
                        </Box>
                      )}
                    </MenuItem>
                  )}

                  <Divider sx={{ borderStyle: "dashed" }} />

                  <MenuItem
                    onClick={() => {
                      dispatch(
                        deleteObject({
                          id: props.step.action.id,
                        })
                      );
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <Iconify icon={"eva:trash-2-outline"} />
                    Delete
                  </MenuItem>
                </>
              }
            />
          </Box>

          <div
            id={"animation_order_" + props.step.action.id}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              opacity: 0,
              backgroundColor: "rgb(144, 238, 144,0.3)",
              zIndex: -1,
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}

export default React.memo(AnimationAction);
